import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Ban, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplyAbonoDialog } from './ApplyAbonoDialog';
import {
  ADD_OPEN_ACCOUNT_CONCEPTS_MUTATION,
  CANCEL_LAST_OPEN_ACCOUNT_MOVEMENT_MUTATION,
  OPEN_ACCOUNT_DETAIL_QUERY,
  SETTLE_OPEN_ACCOUNT_MUTATION,
} from './open-accounts.gql';

import type React from 'react';

import { SellableSearchPanel, type SellableHit } from '@/modules/doctor-pos/SellableSearchPanel';
import {
  OPEN_ACCOUNT_KIND_FROM_GQL,
  OPEN_ACCOUNT_MOVEMENT_KIND_FROM_GQL,
  OPEN_ACCOUNT_MOVEMENT_STATUS_FROM_GQL,
  OPEN_ACCOUNT_SETTLE_STATUS_TO_GQL,
  OPEN_ACCOUNT_STATUS_FROM_GQL,
  SALE_ORDER_ITEM_KIND_TO_GQL,
} from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface OpenAccount {
  id: string;
  folio_number: string | null;
  branch_id: string;
  kind: string;
  status: string;
  balance_cents: string;
  customer_id: string;
  pet_id: string;
  tutor_name: string | null;
  pet_name: string | null;
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
  close_reason: string | null;
}

interface Concept {
  sale_order_id: string;
  sale_folio_number: string | null;
  sale_order_item_id: string;
  line_no: number;
  item_kind: string;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  total_cents: string;
  covered_cents: string;
  pending_cents: string;
  coverage_status: string;
  charged_at: string;
}

interface Charge {
  sale_order_id: string;
  sale_folio_number: string | null;
  total_cents: string;
  paid_cents: string;
  pending_cents: string;
  status: string;
  charged_at: string;
}

interface Abono {
  payment_id: string;
  payment_folio_number: string | null;
  payment_method_name: string;
  amount_cents: string;
  reference_number: string | null;
  captured_at: string;
}

interface Movement {
  id: string;
  kind: string;
  amount_cents: string;
  balance_after_cents: string;
  status: string;
  reference_kind: string | null;
  note: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  created_at: string;
}

interface Detail {
  account: OpenAccount;
  tutor_name: string | null;
  pet_name: string | null;
  pet_species: string | null;
  pet_sex: string | null;
  pet_breed: string | null;
  pet_approximate_age_months: number | null;
  last_current_weight_grams: number | null;
  last_weight_measured_at: string | null;
  total_charged_cents: string;
  total_paid_cents: string;
  balance_cents: string;
  concepts: Concept[];
  charges: Charge[];
  abonos: Abono[];
  movements: Movement[];
}

interface DetailResponse {
  openAccountDetail: Detail | null;
}

interface OpenAccountDetailDialogProps {
  account_id: string;
  on_close: () => void;
}

export function OpenAccountDetailDialog({
  account_id,
  on_close,
}: OpenAccountDetailDialogProps): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const is_admin = user?.roles.includes('administrator') ?? false;
  const can_payment = user?.permissions.includes('open_accounts.payment') ?? false;
  const can_charge = user?.permissions.includes('pos.cashier.use') ?? false;

  const { data, refetch } = useQuery<DetailResponse>(OPEN_ACCOUNT_DETAIL_QUERY, {
    variables: { id: account_id },
    fetchPolicy: 'cache-and-network',
  });

  const [show_abono, setShowAbono] = useState(false);
  const [show_settle, setShowSettle] = useState(false);
  const [show_add_concepts, setShowAddConcepts] = useState(false);
  const [movement_to_cancel, setMovementToCancel] = useState<Movement | null>(null);
  const [error_text, setErrorText] = useState<string | null>(null);

  const detail = data?.openAccountDetail ?? null;
  const account = detail
    ? {
        ...detail.account,
        kind:
          OPEN_ACCOUNT_KIND_FROM_GQL[
            detail.account.kind as keyof typeof OPEN_ACCOUNT_KIND_FROM_GQL
          ] ?? detail.account.kind,
        status:
          OPEN_ACCOUNT_STATUS_FROM_GQL[
            detail.account.status as keyof typeof OPEN_ACCOUNT_STATUS_FROM_GQL
          ] ?? detail.account.status,
      }
    : null;
  const movements = (detail?.movements ?? []).map((movement) => ({
    ...movement,
    kind:
      OPEN_ACCOUNT_MOVEMENT_KIND_FROM_GQL[
        movement.kind as keyof typeof OPEN_ACCOUNT_MOVEMENT_KIND_FROM_GQL
      ] ?? movement.kind,
    status:
      OPEN_ACCOUNT_MOVEMENT_STATUS_FROM_GQL[
        movement.status as keyof typeof OPEN_ACCOUNT_MOVEMENT_STATUS_FROM_GQL
      ] ?? movement.status,
  }));

  const balance = account ? BigInt(account.balance_cents) : 0n;
  const can_apply_abono =
    account?.status === 'open' &&
    can_payment &&
    (account.kind !== 'normal' || balance > 0n);
  const can_add_concepts = account?.status === 'open' && can_charge;
  const can_settle = is_admin && account?.status === 'open' && balance === 0n;
  const last_active_movement_id =
    movements.length > 0 ? (movements[movements.length - 1]?.id ?? null) : null;

  const concepts_by_charge = useMemo(() => {
    const grouped = new Map<string, Concept[]>();
    for (const concept of detail?.concepts ?? []) {
      const list = grouped.get(concept.sale_order_id) ?? [];
      list.push(concept);
      grouped.set(concept.sale_order_id, list);
    }
    return grouped;
  }, [detail?.concepts]);

  const refetchAll = async (): Promise<void> => {
    await refetch();
  };

  if (!detail || !account) {
    return (
      <DialogShell max_width="max-w-md">
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            {t('common.loading')}
          </CardContent>
          <CardFooter className="justify-end">
            <Button type="button" variant="ghost" onClick={on_close}>
              {t('common.cancel')}
            </Button>
          </CardFooter>
        </Card>
      </DialogShell>
    );
  }

  return (
    <>
      <DialogShell max_width="max-w-5xl">
        <Card className="max-h-[92vh] overflow-hidden">
          <CardHeader className="border-border flex flex-row items-start justify-between gap-2 border-b">
            <div>
              <CardTitle className="text-base">
                {t('open_accounts.detail_title')} ·{' '}
                <span className="font-mono text-xs">
                  #{account.folio_number ?? account.id.slice(0, 8)}
                </span>
              </CardTitle>
              <p className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                <span className="block">
                  {t('open_accounts.tutor')}:{' '}
                  <span className="text-foreground">
                    {detail.tutor_name ?? account.tutor_name ?? account.customer_id}
                  </span>
                </span>
                <span className="block">
                  {t('open_accounts.pet')}:{' '}
                  <span className="text-foreground">
                    {detail.pet_name ?? account.pet_name ?? account.pet_id}
                  </span>
                </span>
                <span className="block">
                  {t('open_accounts.last_weight_registered')}:{' '}
                  <span className="text-foreground">
                    {formatWeightKg(detail.last_current_weight_grams)}
                  </span>
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t(`open_accounts.kind_${account.kind}`)}</Badge>
              <Badge variant={account.status === 'open' ? 'success' : 'muted'}>
                {t(`open_accounts.status_${account.status}`)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="max-h-[70vh] space-y-4 overflow-auto py-4">
            {error_text ? (
              <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-xs">
                {error_text}
              </div>
            ) : null}

            <div className="grid gap-3 md:grid-cols-3">
              <SummaryCard
                label={t('open_accounts.total_charged')}
                value={detail.total_charged_cents}
              />
              <SummaryCard
                label={t('open_accounts.total_paid')}
                value={detail.total_paid_cents}
                tone="success"
              />
              <SummaryCard
                label={t('open_accounts.balance')}
                value={detail.balance_cents}
                tone="warning"
              />
            </div>

            <section className="border-border rounded-lg border">
              <div className="border-border flex items-center justify-between border-b px-3 py-2">
                <h4 className="text-sm font-semibold">{t('open_accounts.concepts_title')}</h4>
                <span className="text-muted-foreground text-xs">
                  {detail.concepts.length} {t('open_accounts.concepts_count')}
                </span>
              </div>
              {detail.concepts.length === 0 ? (
                <p className="text-muted-foreground p-3 text-sm">
                  {t('open_accounts.concepts_empty')}
                </p>
              ) : (
                <ul className="divide-border divide-y text-sm">
                  {detail.concepts.map((concept) => (
                    <li
                      key={concept.sale_order_item_id}
                      className="grid gap-2 px-3 py-2 md:grid-cols-[1fr_auto]"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs">{concept.item_code}</span>
                          <span className="font-medium">{concept.item_name}</span>
                          <Badge
                            variant={
                              concept.coverage_status === 'paid'
                                ? 'success'
                                : concept.coverage_status === 'partial'
                                  ? 'default'
                                  : 'muted'
                            }
                          >
                            {t(`open_accounts.coverage_${concept.coverage_status}`)}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {concept.quantity} x {formatCentsString(concept.unit_price_cents)} ·{' '}
                          {t('open_accounts.sale_folio')}: #
                          {concept.sale_folio_number ?? concept.sale_order_id.slice(0, 8)}
                        </p>
                      </div>
                      <div className="text-right font-mono text-xs tabular-nums">
                        <p>{formatCentsString(concept.total_cents)}</p>
                        <p className="text-success">{formatCentsString(concept.covered_cents)}</p>
                        <p className="text-muted-foreground">
                          {formatCentsString(concept.pending_cents)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="grid gap-4 lg:grid-cols-2">
              <section className="border-border rounded-lg border">
                <div className="border-border border-b px-3 py-2">
                  <h4 className="text-sm font-semibold">{t('open_accounts.charges_title')}</h4>
                </div>
                <ul className="divide-border divide-y text-sm">
                  {detail.charges.map((charge) => (
                    <li key={charge.sale_order_id} className="px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs">
                          #{charge.sale_folio_number ?? charge.sale_order_id.slice(0, 8)}
                        </span>
                        <span className="font-mono tabular-nums">
                          {formatCentsString(charge.total_cents)}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-xs">{fmtDate(charge.charged_at)}</p>
                      {(concepts_by_charge.get(charge.sale_order_id) ?? []).length > 0 ? (
                        <p className="text-muted-foreground mt-1 text-xs">
                          {(concepts_by_charge.get(charge.sale_order_id) ?? [])
                            .map((concept) => concept.item_name)
                            .join(', ')}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="border-border rounded-lg border">
                <div className="border-border border-b px-3 py-2">
                  <h4 className="text-sm font-semibold">{t('open_accounts.abonos_title')}</h4>
                </div>
                {detail.abonos.length === 0 ? (
                  <p className="text-muted-foreground p-3 text-sm">
                    {t('open_accounts.abonos_empty')}
                  </p>
                ) : (
                  <ul className="divide-border divide-y text-sm">
                    {detail.abonos.map((abono) => (
                      <li key={abono.payment_id} className="px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span>
                            {abono.payment_method_name}
                            {abono.reference_number ? (
                              <span className="text-muted-foreground ml-1 text-xs">
                                · Ref. {abono.reference_number}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-success font-mono tabular-nums">
                            {formatCentsString(abono.amount_cents)}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          #{abono.payment_folio_number ?? abono.payment_id.slice(0, 8)} ·{' '}
                          {fmtDate(abono.captured_at)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <section className="border-border rounded-lg border">
              <div className="border-border border-b px-3 py-2">
                <h4 className="text-sm font-semibold">{t('open_accounts.movements_title')}</h4>
              </div>
              {movements.length === 0 ? (
                <p className="text-muted-foreground p-3 text-sm">
                  {t('open_accounts.movements_empty')}
                </p>
              ) : (
                <ul className="divide-border max-h-48 divide-y overflow-auto text-sm">
                  {movements.map((movement) => {
                    const can_cancel_movement =
                      is_admin &&
                      account.status === 'open' &&
                      movement.status === 'active' &&
                      movement.id === last_active_movement_id;
                    return (
                      <li
                        key={movement.id}
                        className="flex items-center justify-between gap-2 px-3 py-2"
                      >
                        <div>
                          <Badge
                            variant={
                              movement.kind === 'charge'
                                ? 'destructive'
                                : movement.kind === 'payment'
                                  ? 'success'
                                  : 'muted'
                            }
                          >
                            {t(`open_accounts.kind_${movement.kind}`)}
                          </Badge>
                          <p className="text-muted-foreground mt-1 text-xs">
                            {fmtDate(movement.created_at)}
                            {movement.note ? ` · ${movement.note}` : ''}
                          </p>
                        </div>
                        <div className="text-right font-mono text-xs tabular-nums">
                          <p>{formatCentsString(movement.amount_cents)}</p>
                          <p className="text-muted-foreground">
                            {t('open_accounts.balance_after')}:{' '}
                            {formatCentsString(movement.balance_after_cents)}
                          </p>
                          {can_cancel_movement ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="mt-1 h-7 px-2"
                              aria-label={t('open_accounts.cancel_movement')}
                              onClick={() => setMovementToCancel(movement)}
                            >
                              <Ban className="text-destructive h-4 w-4" aria-hidden />
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </CardContent>
          <CardFooter className="border-border justify-end gap-2 border-t">
            <Button type="button" variant="ghost" onClick={on_close}>
              {t('common.cancel')}
            </Button>
            {can_settle ? (
              <Button type="button" variant="outline" onClick={() => setShowSettle(true)}>
                {t('open_accounts.close_account')}
              </Button>
            ) : null}
            {can_add_concepts ? (
              <Button type="button" variant="outline" onClick={() => setShowAddConcepts(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t('open_accounts.add_concepts')}
              </Button>
            ) : null}
            {can_apply_abono ? (
              <Button type="button" onClick={() => setShowAbono(true)}>
                {t('open_accounts.apply_abono')}
              </Button>
            ) : null}
          </CardFooter>
        </Card>
      </DialogShell>

      {show_abono ? (
        <ApplyAbonoDialog
          account={account}
          on_close={() => setShowAbono(false)}
          on_submitted={async () => {
            setShowAbono(false);
            await refetchAll();
          }}
          on_error={setErrorText}
        />
      ) : null}

      {show_add_concepts ? (
        <AddConceptsDialog
          account={account}
          last_current_weight_grams={detail.last_current_weight_grams}
          pet_species={detail.pet_species}
          pet_sex={detail.pet_sex}
          pet_breed={detail.pet_breed}
          pet_approximate_age_months={detail.pet_approximate_age_months}
          on_close={() => setShowAddConcepts(false)}
          on_submitted={async () => {
            setShowAddConcepts(false);
            await refetchAll();
          }}
          on_error={setErrorText}
        />
      ) : null}

      {show_settle ? (
        <SettleAccountDialog
          account={account}
          on_close={() => setShowSettle(false)}
          on_submitted={async () => {
            setShowSettle(false);
            await refetchAll();
          }}
          on_error={setErrorText}
        />
      ) : null}

      {movement_to_cancel ? (
        <CancelMovementDialog
          account={account}
          movement={movement_to_cancel}
          on_close={() => setMovementToCancel(null)}
          on_submitted={async () => {
            setMovementToCancel(null);
            await refetchAll();
          }}
          on_error={setErrorText}
        />
      ) : null}
    </>
  );
}

function DialogShell({
  children,
  max_width,
}: {
  children: React.ReactNode;
  max_width: string;
}): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <div className={`w-full ${max_width}`}>{children}</div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'warning';
}): JSX.Element {
  const tone_class =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-foreground';
  return (
    <div className="border-border bg-card/40 rounded-lg border p-3">
      <p className="text-muted-foreground text-xs uppercase tracking-wide">{label}</p>
      <p className={`mt-1 font-mono text-lg font-semibold tabular-nums ${tone_class}`}>
        {formatCentsString(value)}
      </p>
    </div>
  );
}

function CancelMovementDialog({
  account,
  movement,
  on_close,
  on_submitted,
  on_error,
}: {
  account: OpenAccount;
  movement: Movement;
  on_close: () => void;
  on_submitted: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [cancelMovement, { loading }] = useMutation(CANCEL_LAST_OPEN_ACCOUNT_MOVEMENT_MUTATION);
  const can_submit = reason.trim().length > 0 && !loading;

  const onSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    try {
      await cancelMovement({
        variables: {
          input: {
            open_account_id: account.id,
            movement_id: movement.id,
            reason: reason.trim(),
          },
        },
      });
      showToast({
        tone: 'success',
        title: t('open_accounts.cancel_movement_success_title'),
        body: t('open_accounts.cancel_movement_success_body'),
      });
      await on_submitted();
    } catch (err) {
      if (err instanceof ApolloError) {
        on_error(mapCancelMovementError(err.graphQLErrors[0]?.message ?? err.message, t));
        return;
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <DialogShell max_width="max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>{t('open_accounts.cancel_movement_title')}</CardTitle>
          <p className="text-muted-foreground text-xs">{t('open_accounts.cancel_movement_hint')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="border-border bg-muted/30 rounded-md border p-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">
                {t(`open_accounts.kind_${movement.kind}`)}
              </span>
              <span className="font-mono tabular-nums">
                {formatCentsString(movement.amount_cents)}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-muted-foreground">{t('open_accounts.balance_after')}</span>
              <span className="font-mono tabular-nums">
                {formatCentsString(movement.balance_after_cents)}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cancel-open-account-movement-reason">
              {t('open_accounts.cancel_movement_reason')}
            </Label>
            <Input
              id="cancel-open-account-movement-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={loading}
              placeholder={t('open_accounts.cancel_movement_reason_placeholder')}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void onSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('open_accounts.cancel_movement_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </DialogShell>
  );
}

interface AddConceptDraft {
  key: string;
  item_kind: 'product' | 'service' | 'kit';
  product_id: string | null;
  service_id: string | null;
  kit_id: string | null;
  item_name: string;
  item_code: string;
  quantity: string;
  unit_price_cents: string;
  price_rule_id: string | null;
  requires_current_weight: boolean;
}

function AddConceptsDialog({
  account,
  last_current_weight_grams,
  pet_species,
  pet_sex,
  pet_breed,
  pet_approximate_age_months,
  on_close,
  on_submitted,
  on_error,
}: {
  account: OpenAccount;
  last_current_weight_grams: number | null;
  pet_species: string | null;
  pet_sex: string | null;
  pet_breed: string | null;
  pet_approximate_age_months: number | null;
  on_close: () => void;
  on_submitted: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<AddConceptDraft[]>([]);
  const [note, setNote] = useState('');
  const [current_weight_kg, setCurrentWeightKg] = useState(() =>
    formatWeightKgInput(last_current_weight_grams),
  );
  const [addConcepts, { loading }] = useMutation(ADD_OPEN_ACCOUNT_CONCEPTS_MUTATION);
  const parsed_current_weight_grams = useMemo(
    () => parseWeightKgToGramsOrNull(current_weight_kg),
    [current_weight_kg],
  );
  const has_invalid_weight = current_weight_kg.trim().length > 0 && !parsed_current_weight_grams;
  const has_weight_required_draft = drafts.some((draft) => draft.requires_current_weight);
  const preview_context = useMemo(
    () => ({
      species: pet_species,
      sex: pet_sex,
      breed: pet_breed,
      approximate_age_months: pet_approximate_age_months,
      current_weight_grams: parsed_current_weight_grams,
      branch_id: account.branch_id,
      customer_id: account.customer_id,
    }),
    [
      account.branch_id,
      account.customer_id,
      pet_species,
      pet_sex,
      pet_breed,
      pet_approximate_age_months,
      parsed_current_weight_grams,
    ],
  );

  const onAdd = (hit: SellableHit, quantity: string): void => {
    if (hit.kind !== 'product' && hit.kind !== 'service' && hit.kind !== 'kit') return;
    const item_kind = hit.kind;
    setDrafts((prev) => [
      ...prev,
      {
        key: `${hit.kind}:${hit.id}:${Date.now()}:${prev.length}`,
        item_kind,
        product_id: hit.kind === 'product' ? hit.id : null,
        service_id: hit.kind === 'service' ? hit.id : null,
        kit_id: hit.kind === 'kit' ? hit.id : null,
        item_name: hit.name,
        item_code: hit.code,
        quantity,
        unit_price_cents: hit.resolved_unit_price_cents ?? hit.price_cents,
        price_rule_id: hit.resolved_price_rule_id ?? null,
        requires_current_weight: hit.requires_current_weight === true,
      },
    ]);
  };

  const can_submit =
    drafts.length > 0 &&
    drafts.every((draft) => Number(draft.quantity) > 0) &&
    !has_invalid_weight &&
    (!has_weight_required_draft || Boolean(parsed_current_weight_grams)) &&
    !loading;

  const onSubmit = async (): Promise<void> => {
    on_error(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const current_weight_grams = parseWeightKgToGrams(current_weight_kg);
      await addConcepts({
        variables: {
          input: {
            open_account_id: account.id,
            lines: drafts.map((draft) => ({
              item_kind: SALE_ORDER_ITEM_KIND_TO_GQL[draft.item_kind],
              product_id: draft.product_id,
              service_id: draft.service_id,
              kit_id: draft.kit_id,
              quantity: draft.quantity,
            })),
            current_weight_grams,
            note: note.trim() || null,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      await on_submitted();
    } catch (err) {
      if (err instanceof ApolloError) {
        on_error(mapAddConceptsError(err.graphQLErrors[0]?.message ?? err.message, t));
        return;
      }
      if (err instanceof Error) {
        on_error(err.message === 'weight_invalid' ? t('doctor_pos.weight_invalid') : err.message);
      }
    }
  };

  return (
    <DialogShell max_width="max-w-2xl">
      <Card className="max-h-[92vh] overflow-hidden">
        <CardHeader>
          <CardTitle>{t('open_accounts.add_concepts_title')}</CardTitle>
        </CardHeader>
        <CardContent className="max-h-[70vh] space-y-3 overflow-auto">
          {/* Keep cashier add-concepts independent from doctor pricing preview permissions. */}
          <SellableSearchPanel
            onAdd={onAdd}
            disabled={loading}
            allowed_kinds={['product', 'service', 'kit']}
            preview_context={preview_context}
          />
          {drafts.length > 0 ? (
            <ul className="divide-border border-border divide-y rounded-md border text-sm">
              {drafts.map((draft, index) => (
                <li key={draft.key} className="flex items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      <span className="font-mono text-xs">{draft.item_code}</span> ·{' '}
                      {draft.item_name}
                      {draft.requires_current_weight ? (
                        <Badge variant="outline" className="border-primary text-primary ml-2">
                          {t('doctor_pos.requires_weight_badge')}
                        </Badge>
                      ) : null}
                    </p>
                    <p className="text-muted-foreground font-mono text-xs">
                      {formatCentsString(draft.unit_price_cents)}
                      {draft.price_rule_id ? (
                        <span className="text-primary ml-1">
                          {t('open_accounts.price_rule_applied')}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <Input
                    className="w-24 font-mono"
                    value={draft.quantity}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDrafts((prev) =>
                        prev.map((item, item_index) =>
                          item_index === index ? { ...item, quantity: value } : item,
                        ),
                      );
                    }}
                    disabled={loading}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setDrafts((prev) => prev.filter((_, item_index) => item_index !== index))
                    }
                    disabled={loading}
                  >
                    <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="border-border text-muted-foreground rounded-md border border-dashed p-3 text-sm">
              {t('open_accounts.add_concepts_empty')}
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor="open-account-current-weight">
              {t('open_accounts.current_weight_kg')}
            </Label>
            <Input
              id="open-account-current-weight"
              type="text"
              inputMode="decimal"
              value={current_weight_kg}
              onChange={(event) => setCurrentWeightKg(event.target.value)}
              disabled={loading}
              placeholder="4.500"
            />
            {has_weight_required_draft && !parsed_current_weight_grams ? (
              <p className="text-primary text-xs">
                {t('doctor_pos.error_current_weight_required')}
              </p>
            ) : has_invalid_weight ? (
              <p className="text-destructive text-xs">{t('doctor_pos.weight_invalid')}</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="open-account-concepts-note">{t('open_accounts.note')}</Label>
            <Input
              id="open-account-concepts-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={loading}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!can_submit}
            onClick={() => {
              void onSubmit();
            }}
          >
            {loading ? t('common.loading') : t('open_accounts.add_concepts')}
          </Button>
        </CardFooter>
      </Card>
    </DialogShell>
  );
}

interface SettleProps {
  account: OpenAccount;
  on_close: () => void;
  on_submitted: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

function SettleAccountDialog({
  account,
  on_close,
  on_submitted,
  on_error,
}: SettleProps): JSX.Element {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'settled' | 'transferred' | 'deceased' | 'cancelled'>(
    'settled',
  );
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [settleOpenAccount, { loading }] = useMutation(SETTLE_OPEN_ACCOUNT_MUTATION);

  const balance = BigInt(account.balance_cents);
  const requires_force = balance > 0n;
  const can_submit = reason.trim().length > 0 && (!requires_force || force) && !loading;

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    try {
      await settleOpenAccount({
        variables: {
          input: {
            open_account_id: account.id,
            status: OPEN_ACCOUNT_SETTLE_STATUS_TO_GQL[status],
            close_reason: reason.trim(),
            force_with_balance: force,
          },
        },
      });
      await on_submitted();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code.startsWith('open_account_has_balance')) {
          on_error(t('open_accounts.error_has_balance'));
          return;
        }
        if (code.startsWith('open_account_not_open')) {
          on_error(t('open_accounts.error_not_open'));
          return;
        }
        on_error(code);
        return;
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <DialogShell max_width="max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>{t('open_accounts.close_account_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="settle-status">{t('open_accounts.settle_status')}</Label>
            <select
              id="settle-status"
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as 'settled' | 'transferred' | 'deceased' | 'cancelled')
              }
              disabled={loading}
            >
              <option value="settled">{t('open_accounts.status_settled')}</option>
              <option value="transferred">{t('open_accounts.status_transferred')}</option>
              <option value="deceased">{t('open_accounts.status_deceased')}</option>
              <option value="cancelled">{t('open_accounts.status_cancelled')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="settle-reason">{t('open_accounts.close_reason')}</Label>
            <Input
              id="settle-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
            />
          </div>
          {requires_force ? (
            <label className="text-warning flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                className="mt-0.5"
                disabled={loading}
              />
              <span>
                {t('open_accounts.force_with_balance', {
                  balance: formatCentsString(account.balance_cents),
                })}
              </span>
            </label>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('open_accounts.close_account')}
          </Button>
        </CardFooter>
      </Card>
    </DialogShell>
  );
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatWeightKg(grams: number | null): string {
  if (!grams) return '—';
  return `${formatWeightKgInput(grams)} kg`;
}

function formatWeightKgInput(grams: number | null): string {
  if (!grams) return '';
  return (grams / 1000).toFixed(3);
}

function parseWeightKgToGrams(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0 || value > 5000) {
    throw new Error('weight_invalid');
  }
  const grams = Math.round(value * 1000);
  if (!Number.isInteger(grams) || grams <= 0) throw new Error('weight_invalid');
  return grams;
}

function parseWeightKgToGramsOrNull(input: string): number | null {
  try {
    return parseWeightKgToGrams(input);
  } catch {
    return null;
  }
}

function mapAddConceptsError(code: string, t: (key: string) => string): string {
  if (code === 'current_weight_required' || code.startsWith('current_weight_required:')) {
    return t('doctor_pos.error_current_weight_required');
  }
  if (code === 'current_weight_must_be_positive_int') {
    return t('doctor_pos.weight_invalid');
  }
  if (code === 'cash_shift_required') {
    return t('open_accounts.error_shift_required');
  }
  if (code.startsWith('open_account_not_open')) {
    return t('open_accounts.error_not_open');
  }
  return code;
}

function mapCancelMovementError(code: string, t: (key: string) => string): string {
  if (code === 'open_account_movement_not_latest') {
    return t('open_accounts.error_movement_not_latest');
  }
  if (code === 'open_account_movement_not_found') {
    return t('open_accounts.error_movement_not_found');
  }
  if (code.startsWith('open_account_not_open')) {
    return t('open_accounts.error_not_open');
  }
  if (code.startsWith('payment_not_captured')) {
    return t('open_accounts.error_payment_not_captured');
  }
  if (code.startsWith('order_not_cancellable')) {
    return t('open_accounts.error_sale_not_cancellable');
  }
  if (code.startsWith('sale_has_active_invoice')) {
    return t('open_accounts.error_sale_has_active_invoice');
  }
  if (code === 'cancel_reason_required') {
    return t('open_accounts.error_reason_required');
  }
  return code;
}
